// components/patients/PatientList.tsx - FIXED VERSION
import React, { useState, useEffect } from "react";
import PatientCard, { Patient } from "./PatientCard";
import PatientService from "../../services/patientService";

interface PatientListProps {
  onEditPatient?: (patient: Patient) => void;
  onViewPatient?: (patient: Patient) => void;
  refreshTrigger?: number; // To trigger refresh from parent
}

const PatientList: React.FC<PatientListProps> = ({
  onEditPatient,
  onViewPatient,
  refreshTrigger,
}) => {
  const [patients, setPatients] = useState<Patient[]>([]); // ✅ Initialize as empty array
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterGender, setFilterGender] = useState<string>("");
  const [sortBy, setSortBy] = useState<"name" | "date" | "age">("name");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterCity, setFilterCity] = useState<string>("");
  const [filterStarred, setFilterStarred] = useState<string>("");
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load patients from Laravel API
  useEffect(() => {
    const loadPatients = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await PatientService.getPatients({
          search: debouncedSearchTerm || undefined,
          gender: filterGender || undefined,
          city: filterCity || undefined, // Add this line
          is_starred:
            filterStarred === "true"
              ? true
              : filterStarred === "false"
                ? false
                : undefined,
          is_active: true,
          sort_by:
            sortBy === "name"
              ? "first_name"
              : sortBy === "age"
                ? "birth_date"
                : "created_at",
          sort_order: sortBy === "age" ? "asc" : "desc",
        });

        if (response && response.data && Array.isArray(response.data)) {
          setPatients(response.data);
        } else {
          console.error("Invalid API response format:", response);
          setPatients([]);
        }
      } catch (error: any) {
        console.error("Failed to load patients:", error);
        setError(error.message || "Failed to load patients");
        setPatients([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadPatients();
  }, [
    debouncedSearchTerm,
    filterGender,
    filterCity, // Add this line
    filterStarred,
    sortBy,
    refreshTrigger,
  ]);
  const handleStarToggle = (patientId: number, newStarredState: boolean) => {
    // Update the local state immediately for better UX
    setPatients((prev) =>
      prev.map((patient) =>
        patient.id === patientId
          ? { ...patient, is_starred: newStarredState }
          : patient,
      ),
    );
  };
  // Delete patient
  const handleDeletePatient = async (patientId: number) => {
    try {
      await PatientService.deletePatient(patientId);
      setPatients((prev) => prev.filter((p) => p.id !== patientId));
    } catch (error: any) {
      console.error("Failed to delete patient:", error);
      alert(error.message || "Failed to delete patient. Please try again.");
    }
  };

  // Filter patients (for instant frontend feedback while typing)
  const filteredPatients = patients.filter((patient) => {
    // If there's a search term, backend already filtered - just apply gender filter
    if (debouncedSearchTerm) {
      const matchesGender = !filterGender || patient.gender === filterGender;
      return matchesGender;
    }

    // If no search term, apply all frontend filters
    const matchesGender = !filterGender || patient.gender === filterGender;

    // For instant feedback while typing (before debounce), do basic name matching
    if (searchTerm && searchTerm !== debouncedSearchTerm) {
      const matchesSearch =
        patient.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.phone.includes(searchTerm) ||
        (patient.email &&
          patient.email.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesSearch && matchesGender;
    }

    return matchesGender;
  });
  const containerStyle: React.CSSProperties = {
    padding: "24px",
    maxWidth: "1200px",
    margin: "0 auto",
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: "24px",
  };

  const titleStyle: React.CSSProperties = {
    fontSize: "28px",
    fontWeight: "bold",
    color: "#1e293b",
    marginBottom: "8px",
  };

  const subtitleStyle: React.CSSProperties = {
    color: "#64748b",
    fontSize: "16px",
  };

  const filtersStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
    padding: "20px",
    background: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
  };

  const inputStyle: React.CSSProperties = {
    padding: "12px 16px",
    border: "2px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s ease",
  };

  const statsStyle: React.CSSProperties = {
    display: "flex",
    gap: "16px",
    marginBottom: "24px",
  };

  const statCardStyle: React.CSSProperties = {
    background: "#ffffff",
    padding: "16px",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
    textAlign: "center",
    minWidth: "120px",
  };

  const statNumberStyle: React.CSSProperties = {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#3b82f6",
    marginBottom: "4px",
  };

  const statLabelStyle: React.CSSProperties = {
    fontSize: "14px",
    color: "#64748b",
  };

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
    gap: "20px",
  };

  const loadingStyle: React.CSSProperties = {
    textAlign: "center",
    padding: "40px",
    fontSize: "16px",
    color: "#64748b",
  };

  const emptyStyle: React.CSSProperties = {
    textAlign: "center",
    padding: "40px",
    color: "#64748b",
  };

  // // ✅ Show debug info in development
  // const debugStyle: React.CSSProperties = {
  //   background: "#f3f4f6",
  //   padding: "12px",
  //   borderRadius: "8px",
  //   fontSize: "12px",
  //   marginBottom: "16px",
  //   color: "#4b5563",
  // };

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{ ...emptyStyle, color: "#ef4444" }}>
          <h3>Error loading patients</h3>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: "16px",
              padding: "8px 16px",
              background: "#ef4444",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h1 style={titleStyle}>Patients</h1>
        <p style={subtitleStyle}>Manage and view all patient records</p>
      </div>

      {/* Debug Info (remove in production) */}
      {/* {process.env.NODE_ENV === "development" && (
        <div style={debugStyle}>
          <strong>Debug Info:</strong> Patients array length: {patients.length}{" "}
          | Loading: {isLoading ? "Yes" : "No"} | Error: {error || "None"} |
          Search: "{debouncedSearchTerm}" | Filter: "{filterGender}" | Sort:{" "}
          {sortBy}
        </div>
      )} */}

      {/* Statistics */}
      <div style={statsStyle}>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>{patients.length}</div>
          <div style={statLabelStyle}>Total Patients</div>
        </div>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>
            {patients.filter((p) => p.is_active).length}
          </div>
          <div style={statLabelStyle}>Active</div>
        </div>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>
            {patients.filter((p) => p.gender === "male").length}
          </div>
          <div style={statLabelStyle}>Male</div>
        </div>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>
            {patients.filter((p) => p.gender === "female").length}
          </div>
          <div style={statLabelStyle}>Female</div>
        </div>
        {/* Add City Statistics */}
        <div style={statCardStyle}>
          <div style={statNumberStyle}>
            {
              patients.filter(
                (p) => p.address && p.address.toLowerCase().includes("tripoli"),
              ).length
            }
          </div>
          <div style={statLabelStyle}>Tripoli</div>
        </div>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>
            {
              patients.filter(
                (p) => p.address && p.address.toLowerCase().includes("beirut"),
              ).length
            }
          </div>
          <div style={statLabelStyle}>Beirut</div>
        </div>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>
            {patients.filter((p) => p.is_starred).length}
          </div>
          <div style={statLabelStyle}>Starred</div>
        </div>
      </div>

      {/* Filters */}
      <div style={filtersStyle}>
        <div>
          <label
            htmlFor="search_contact"
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Search Patients
          </label>
          <input
            type="text"
            id="search_contact"
            name="search_contact"
            placeholder="Search by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div>
          <label
            htmlFor="filter_gender"
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Filter by Gender
          </label>
          <select
            id="filter_gender"
            name="filter_gender"
            value={filterGender}
            onChange={(e) => setFilterGender(e.target.value)}
            style={inputStyle}
          >
            <option value="">All Genders</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>
        <div>
          <label
            htmlFor="filter_starred"
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Filter by Priority
          </label>
          <select
            id="filter_starred"
            value={filterStarred}
            onChange={(e) => setFilterStarred(e.target.value)}
            style={inputStyle}
          >
            <option value="">All Patients</option>
            <option value="true">⭐ Starred Only</option>
            <option value="false">Regular Patients</option>
          </select>
        </div>
        <div>
          <label
            htmlFor="filter_city"
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Filter by City
          </label>
          <select
            id="filter_city"
            name="filter_city"
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            style={inputStyle}
          >
            <option value="">All Cities</option>
            <option value="Tripoli">Tripoli</option>
            <option value="Beirut">Beirut</option>
            <option value="Other">Other</option>
          </select>
        </div>
        {/* <div>
          <label
            htmlFor="sortBy"
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Sort By
          </label>
          <select
            id="sortBy"
            value={sortBy}
            onChange={(e) =>
              setSortBy(e.target.value as "name" | "date" | "age")
            }
            style={inputStyle}
          >
            <option value="name">Name (A-Z)</option>
            <option value="date">Recently Added</option>
            <option value="age">Age (Youngest First)</option>
          </select>
        </div> */}
      </div>

      {/* Patient List */}
      {isLoading ? (
        <div style={loadingStyle}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #e2e8f0",
              borderTop: "4px solid #3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px auto",
            }}
          />
          Loading patients...
        </div>
      ) : filteredPatients.length === 0 ? (
        <div style={emptyStyle}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>👥</div>
          <h3 style={{ marginBottom: "8px" }}>No patients found</h3>
          <p>
            {searchTerm || filterGender
              ? "Try adjusting your search filters."
              : "Start by adding your first patient."}
          </p>
        </div>
      ) : (
        <div style={gridStyle}>
          {filteredPatients.map((patient) => (
            <PatientCard
              key={patient.id}
              patient={patient}
              onEdit={onEditPatient}
              onView={onViewPatient}
              onDelete={handleDeletePatient}
              onStarToggle={handleStarToggle}
            />
          ))}
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          input:focus, select:focus {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
          }
        `}
      </style>
    </div>
  );
};

export default PatientList;
